// ===========================================
// STRIPE PROVIDER
// ===========================================

import { ReactNode, useMemo } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, ElementsConsumer } from '@stripe/react-stripe-js';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

// Create Stripe instance outside of component to avoid recreation
let stripePromise: Promise<Stripe | null> | null = null;

function getStripe() {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
}

// Stripe Elements appearance configuration
const appearance = {
  theme: 'stripe' as const,
  variables: {
    colorPrimary: '#4f46e5', // indigo-600
    colorBackground: '#ffffff',
    colorText: '#1e293b', // slate-800
    colorDanger: '#dc2626', // red-600
    fontFamily: 'Inter, system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px',
  },
  rules: {
    '.Input': {
      border: '1px solid #e2e8f0',
      boxShadow: 'none',
      padding: '12px',
    },
    '.Input:focus': {
      border: '2px solid #4f46e5',
      boxShadow: 'none',
    },
    '.Label': {
      fontWeight: '500',
      color: '#475569',
      marginBottom: '4px',
    },
    '.Error': {
      color: '#dc2626',
      fontSize: '14px',
      marginTop: '4px',
    },
  },
};

interface StripeProviderProps {
  children: ReactNode;
  clientSecret: string;
}

export function StripeProvider({ children, clientSecret }: StripeProviderProps) {
  const options = useMemo(
    () => ({
      clientSecret,
      appearance,
    }),
    [clientSecret]
  );

  const stripeInstance = getStripe();

  if (!stripeInstance) {
    return (
      <div className="p-4 text-center text-red-600">
        Stripe is not configured. Please add VITE_STRIPE_PUBLISHABLE_KEY to environment.
      </div>
    );
  }

  return (
    <Elements stripe={stripeInstance} options={options}>
      {children}
    </Elements>
  );
}

// HOC for accessing Stripe and Elements in class components
export function withStripe<P extends object>(
  WrappedComponent: React.ComponentType<P & { stripe: Stripe | null; elements: any }>
) {
  return function WithStripeComponent(props: P) {
    return (
      <ElementsConsumer>
        {({ stripe, elements }) => (
          <WrappedComponent {...props} stripe={stripe} elements={elements} />
        )}
      </ElementsConsumer>
    );
  };
}

export { getStripe };
